/**
 * module-list-export.ts — etykiety i klucze eksportu CSV listy kartoteki własnej (E16).
 *
 * Tekst większości komórek wyciąga wspólny `columnCellText` z `renderCell` kolumny. Cztery rodzaje
 * komórek ekran pokazuje jednak z danych wczytanych osobno (nie z `renderCell`), więc dla nich
 * podmieniamy tekst na etykietę z tych samych opcji, które ekran ma już w pamięci:
 *  - kategoria rekordu → nazwa kategorii,
 *  - pole „użytkownik" → imię i nazwisko z listy użytkowników,
 *  - pole „relacja" → nazwa rekordu docelowego,
 *  - pole wyboru ze słownikiem → nazwa pozycji słownika (nie kod).
 * Wartość spoza opcji zostaje surowa (jak na ekranie), brak wartości → pusta komórka.
 */

import type { ModuleField } from '@/components/fields/FieldRegistry';
import type { ListFilterOption } from '@/lib/ext-list-columns';

import type { ModuleListCategory, ModuleListFilterOptions } from './module-list-columns';

type Row = { id: string; [key: string]: unknown };

/** Pola wyboru, których źródłem opcji bywa słownik (jak `moduleFieldFilter`). */
const OPTION_FIELD_TYPES = new Set([
  'select',
  'radio',
  'status',
  'multiselect',
  'tags',
  'checkboxgroup',
]);

/** Wartości komórki (jedna albo tablica) jako lista niepustych tekstów. */
function toValues(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value];
  return raw
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : ''))
    .filter((v) => v !== '');
}

/** Tekst komórki: etykiety z `options` (wartość spoza opcji → surowa), wiele wartości po przecinku. */
function labelsFromOptions(value: unknown, options: ReadonlyArray<ListFilterOption>): string {
  const byValue = new Map(options.map((o) => [o.value, o.label]));
  return toValues(value)
    .map((v) => byValue.get(v) ?? v)
    .join(', ');
}

export interface ModuleExportTextArgs {
  /** WSZYSTKIE aktywne pola kartoteki (typ, slug, słownik). */
  fields: ReadonlyArray<ModuleField>;
  /** Kategorie rekordów modułu (kolumna „Kategoria"). */
  categories: ReadonlyArray<ModuleListCategory>;
  /** Opcje wczytane na ekranie: użytkownicy, relacje, pozycje słowników. */
  filterOptions: ModuleListFilterOptions;
}

/**
 * Nadpisania tekstu komórek wg id kolumny. Kolumna dostaje nadpisanie tylko wtedy, gdy ekran ma
 * dla niej opcje — bez opcji zostaje tekst z `renderCell` (jak na ekranie).
 */
export function moduleExportTextOverrides(
  args: ModuleExportTextArgs,
): Record<string, (row: Row) => string> {
  const { fields, categories, filterOptions } = args;
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  // Nazwa i numer rekordu: komórka na ekranie ma dopiski (np. „Zarchiwizowany") — do pliku sam tekst.
  const out: Record<string, (row: Row) => string> = {
    record_name: (row) => text(row.record_name),
    record_number: (row) => text(row.record_number),
  };

  if (categories.length > 0) {
    const byId = new Map(categories.map((c) => [c.id, c.name]));
    out.category = (row) => {
      const id = row.category_id;
      return typeof id === 'string' ? (byId.get(id) ?? id) : '';
    };
  }

  for (const field of fields) {
    let options: ReadonlyArray<ListFilterOption> | undefined;
    if (field.fieldType === 'user') options = filterOptions.userOptions;
    else if (field.fieldType === 'relation') options = filterOptions.relationOptions[field.slug];
    else if (
      OPTION_FIELD_TYPES.has(field.fieldType) &&
      (field.dictionaryCode?.trim() ?? '') !== ''
    ) {
      options = filterOptions.dictionaryOptions[field.slug];
    }
    if (!options || options.length === 0) continue;
    const resolved = options;
    out[field.slug] = (row) => labelsFromOptions(row[field.slug], resolved);
  }
  return out;
}

/**
 * Id kolumn tabeli → klucze wiersza, o które prosimy serwer (`columns` trasy eksportu). Kolumna
 * „Kategoria" (id `category`) czyta pole wiersza `category_id`; pozostałe id to klucze wprost
 * (`record_name`, `record_number`, slugi pól).
 */
export function moduleExportKeys(columnIds: readonly string[]): string[] {
  return columnIds.map((id) => (id === 'category' ? 'category_id' : id));
}
